/* Availability windows for the `available-rooms` endpoint.

   Occupancy is compared as datetimes on the server (see bookings/occupancy.py),
   so every availability query has to carry the stay's hours. Sending bare dates
   makes the server fall back to 12:00-in / 10:00-out, which is wrong twice over:
   a same-day (day-use) booking inverts into an empty window and the request
   400s, and a late checkout is understated so the room looks free when it isn't.

   The 12:00/10:00 fallbacks here are only for legacy rows whose time was never
   recorded — they mirror DEFAULT_ARRIVAL_TIME / DEFAULT_DEPARTURE_TIME on the
   server and what `bookingToApi` already sends for a blank time. Keep all three
   in step. */

export const DEFAULT_ARRIVAL_TIME = "12:00:00";
export const DEFAULT_DEPARTURE_TIME = "10:00:00";

/* "HH:mm" | "HH:mm:ss" | "" → "HH:mm:ss", falling back when unset. */
function normalizeTime(value, fallback) {
  if (!value) return fallback;
  return /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
}

/* A naive local ISO datetime — no timezone suffix, matching how the server
   stores and compares stay windows. Returns "" when there's no date to anchor
   to, so callers can treat the window as unusable. */
export function toStayDateTime(date, time, fallbackTime) {
  if (!date) return "";
  return `${date}T${normalizeTime(time, fallbackTime)}`;
}

/* The booking's own [start, end) window, for "what can this booking be put
   into?" queries (confirm, change room, add room). */
export function bookingStayWindow(booking) {
  return {
    start: toStayDateTime(booking?.checkIn, booking?.arrivalTime, DEFAULT_ARRIVAL_TIME),
    end: toStayDateTime(booking?.checkOut, booking?.departureTime, DEFAULT_DEPARTURE_TIME),
  };
}

/* The window a later departure would add: from where the room's stay currently
   ends to where it would end instead. Each end carries its own hour — a room's
   departure time can differ from the booking's — falling back to the booking's. */
export function extensionWindow(booking, currentDeparture, newDeparture, currentTime, newTime) {
  const fallback = booking?.departureTime;
  return {
    start: toStayDateTime(currentDeparture, currentTime || fallback, DEFAULT_DEPARTURE_TIME),
    end: toStayDateTime(newDeparture, newTime || fallback, DEFAULT_DEPARTURE_TIME),
  };
}

/* One assigned room's own [start, end) window. Each room on a booking can be
   held for a shorter, staggered slice of it (BookingRoomAssignment.expected_
   checkin/checkout on the server), and either end can also fall outside the
   booking's own. A null end inherits the booking's, exactly as it does server
   side, so a room that was never staggered reads as the whole stay. */
export function assignmentStayWindow(booking, assignment) {
  if (!assignment) return bookingStayWindow(booking);
  return {
    start: toStayDateTime(
      assignment.effectiveArrival || booking?.checkIn,
      assignment.effectiveArrivalTime || booking?.arrivalTime,
      DEFAULT_ARRIVAL_TIME,
    ),
    end: toStayDateTime(
      assignment.effectiveDeparture || booking?.checkOut,
      assignment.effectiveDepartureTime || booking?.departureTime,
      DEFAULT_DEPARTURE_TIME,
    ),
  };
}

/* The window a change-room target has to be free for: the room's own, at both
   ends, and only from now on once that stay is under way. `change_room`
   (bookings/views.py) checks exactly this window — `remaining_window(old_assignment)`
   — and exempts only the assignment being vacated, so the picker and the POST
   agree.

   Both ends move, and both move either way: a room's stay can start later than
   the booking's arrival and end earlier than its departure. Asking about the
   booking's whole stay instead hid rooms the server would have accepted — and
   so did asking about the part already over, which hid any room another guest
   checked out of earlier in this stay. */
export function roomChangeWindow(booking, assignment, now = new Date()) {
  const own = assignmentStayWindow(booking, assignment);
  const current = localStayNow(now);
  if (!own.start || !own.end || current <= own.start || current >= own.end) return own;
  return { start: current, end: own.end };
}

/* "Now" as a naive local stay datetime — the same shape `toStayDateTime`
   produces, so the two compare as plain strings. Rounded *up* to the next
   5-minute mark: TimeInput12h only offers 5-minute steps, and a later start
   can never create a conflict the exact minute would not. */
export function localStayNow(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  const step = 5 * 60 * 1000;
  now = new Date(Math.ceil(now.getTime() / step) * step);
  return (
    `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}` +
    `T${p(now.getHours())}:${p(now.getMinutes())}:00`
  );
}

/* The window an Add Room picker asks about. A room added to a booking that has
   already begun only needs to be free from *now* on — asking about the whole
   stay hides any room that freed up part-way through it, including one this
   same booking held earlier and checked out of. The end stays the booking's.
   Once the booking's end has passed there is no "from now" left, so the
   booking's own window is returned and the server's check decides. */
export function addRoomWindow(booking, now = new Date()) {
  const own = bookingStayWindow(booking);
  const current = localStayNow(now);
  if (!own.start || !own.end || current <= own.start || current >= own.end) return own;
  return { start: current, end: own.end };
}

/* Splits a naive stay datetime back into the { date, time } a form shows. */
export function splitStayDateTime(value) {
  if (!value) return { date: "", time: "" };
  const [date, time = ""] = value.split("T");
  return { date, time: time.slice(0, 5) };
}

/* Per-room stay windows staged in the allocation / add-room pickers,
   { [roomId]: { inDate, inTime, outDate, outTime } }, as the `room_windows`
   payload. A blank field sends nothing, so the room inherits the booking's
   window. */
export function buildRoomWindows(drafts) {
  const out = {};
  Object.entries(drafts || {}).forEach(([roomId, d]) => {
    if (!d) return;
    const checkin = d.inDate ? toStayDateTime(d.inDate, d.inTime, DEFAULT_ARRIVAL_TIME) : null;
    const checkout = d.outDate ? toStayDateTime(d.outDate, d.outTime, DEFAULT_DEPARTURE_TIME) : null;
    if (!checkin && !checkout) return;
    out[roomId] = {
      ...(checkin ? { expected_checkin: checkin } : {}),
      ...(checkout ? { expected_checkout: checkout } : {}),
    };
  });
  return Object.keys(out).length ? out : null;
}

/* `room_windows` for an Add Room POST. The server checks each room against
   exactly what is sent, so the picker's window has to travel with every room
   it offered — otherwise the room inherits the booking's whole stay and the
   POST refuses what the dropdown just listed.

   Each end resolves on its own: a room's staggered draft wins, then the
   picker's window where it differs from the booking's. An end that matches the
   booking's is left out, so the room keeps inheriting it (NULL = inherit). */
export function addRoomPayloadWindows(roomIds, window, booking, drafts = {}) {
  const own = bookingStayWindow(booking);
  const staged = buildRoomWindows(
    Object.fromEntries(roomIds.map((id) => [id, drafts[id]])),
  ) || {};
  const out = {};
  roomIds.forEach((id) => {
    const draft = staged[id] || {};
    const checkin = draft.expected_checkin
      || (window?.start && window.start !== own.start ? window.start : null);
    const checkout = draft.expected_checkout
      || (window?.end && window.end !== own.end ? window.end : null);
    if (!checkin && !checkout) return;
    out[id] = {
      ...(checkin ? { expected_checkin: checkin } : {}),
      ...(checkout ? { expected_checkout: checkout } : {}),
    };
  });
  return Object.keys(out).length ? out : null;
}

/* Rooms the server would refuse to add again: a booking may only take back a
   room it already held where that guest house has opted in to re-use. Any
   prior stay counts, checked out or not. Returns the kept options and the
   hidden ones, so the picker can say why a room is missing. */
export function splitReusableRooms(options, booking, guestHouses) {
  const onBooking = new Set(
    (booking?.roomAssignments || []).map((a) => Number(a.roomId)),
  );
  const reuseByGh = new Map(
    (guestHouses || []).map((gh) => [Number(gh.id), Boolean(gh.allowRoomReuse)]),
  );
  const kept = [];
  const hidden = [];
  (options || []).forEach((r) => {
    const refused = onBooking.has(Number(r.id)) && !reuseByGh.get(Number(r.guestHouseId));
    (refused ? hidden : kept).push(r);
  });
  return { kept, hidden };
}
